import Icon from 'components/atoms/Icon';
import NoItemsDisplay from 'components/molecules/NoItemsDisplay/NoItemsDisplay';
import { ItemsCount } from 'components/ui/ItemsCount/ItemsCount';
import {
  RelationDepth,
  useStageCandidateFindManyQuery,
} from 'generated/graphql';
import { FC, useCallback, useMemo, useState } from 'react';
import { getStages } from 'views/User/UserDetails/UserMainResets/UserMainReset.helpers';
import {
  Button,
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { IUserInfo } from '../UserDetails.interface';
import styles from './UserMainReset.module.scss';
import UserReset from './UserReset/UserResets';

const UserMainReset: FC<IUserInfo> = ({ userInfo }) => {
  const { handleMsgType } = useNotification();
  const [openAllCards, setOpenAllCards] = useState(false);
  const [manuallyToggledCardsSet, setManuallyToggledCardsSet] = useState<
    Set<number>
  >(new Set());

  const {
    data: stageCandidateFindManyResponse,
    loading: stageCandidateFindManyLoading,
  } = useStageCandidateFindManyQuery({
    onError: error => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
    fetchPolicy: 'cache-and-network',
    variables: {
      candidateId: userInfo?.id,
      relationDepth: RelationDepth.FullWithExternal,
    },
    onCompleted: data => {
      if (data?.StageCandidates?.length && data?.StageCandidates?.length > 3) {
        setManuallyToggledCardsSet(new Set());
      } else {
        setManuallyToggledCardsSet(
          new Set(data.StageCandidates?.map((_, index) => index))
        );
      }
    },
  });

  const allCards = useMemo(
    () =>
      new Set(
        stageCandidateFindManyResponse?.StageCandidates?.map(
          (_, index) => index
        ) || []
      ),
    [stageCandidateFindManyResponse?.StageCandidates]
  );

  const handleToggleAllCards = useCallback(() => {
    const newValue = !openAllCards;
    if (newValue) {
      setManuallyToggledCardsSet(allCards);
    } else {
      setManuallyToggledCardsSet(new Set());
    }
    setOpenAllCards(newValue);
  }, [openAllCards, allCards]);

  const handleToggleCard = useCallback((index: number) => {
    setManuallyToggledCardsSet(prev => {
      const newSet = new Set(prev);
      newSet.has(index) ? newSet.delete(index) : newSet.add(index);
      return newSet;
    });
  }, []);

  const isCardOpen = useCallback(
    (index: number) => manuallyToggledCardsSet.has(index),
    [manuallyToggledCardsSet]
  );

  const stages = useMemo(() => {
    return getStages(stageCandidateFindManyResponse?.StageCandidates);
  }, [stageCandidateFindManyResponse?.StageCandidates]);

  if (stageCandidateFindManyLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader variant="bubbles" />
      </div>
    );
  }

  if (stageCandidateFindManyResponse?.StageCandidates) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <h2 className={styles.title}>Assessment stages</h2>
            <ItemsCount count={stages.length} />
          </div>

          <Button
            variant="tertiary"
            size="small"
            leftIcon={<Icon icon={openAllCards ? 'hide_eye' : 'eye'} />}
            onClick={handleToggleAllCards}
            disabled={stages.length === 0}
          >
            {openAllCards ? 'Hide all cards' : 'Show all cards'}
          </Button>
        </div>
        <div className={styles.cardsContainer}>
          {stages.length > 0 ? (
            stages.map((item, index) => {
              return (
                <div key={item?.id} className={styles.card}>
                  <UserReset
                    userInfo={userInfo}
                    isCardOpen={isCardOpen(index)}
                    setIsCardOpen={() => handleToggleCard(index)}
                    {...item}
                  />
                </div>
              );
            })
          ) : (
            <NoItemsDisplay label="stages" />
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default UserMainReset;
